import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Shield, Send, Link2, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700',
  sales: 'bg-sky-100 text-sky-700',
  project_manager: 'bg-violet-100 text-violet-700',
  foreman: 'bg-amber-100 text-amber-700',
  permit_tech: 'bg-teal-100 text-teal-700',
  accounting: 'bg-emerald-100 text-emerald-700',
};

const STATUS_BADGES = {
  active: { icon: CheckCircle, className: 'bg-emerald-50 text-emerald-700', label: 'Active' },
  invited: { icon: Clock, className: 'bg-amber-50 text-amber-700', label: 'Invited' },
  disabled: { icon: XCircle, className: 'bg-red-50 text-red-600', label: 'Disabled' },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'sales' });
  const [selectedRole, setSelectedRole] = useState(null);

  const load = () => {
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/users/roles').then(r => setRoles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, { name: form.name, email: form.email, role: form.role });
        toast.success('User updated');
      } else {
        const r = await api.post('/users', { name: form.name, email: form.email, role: form.role, send_invite: true });
        if (r.data.email_sent) {
          toast.success(`Invite sent to ${form.email}`);
        } else {
          toast.success('User created. Invite email could not be sent — copy the invite link manually.', { duration: 6000 });
        }
      }
      setDialogOpen(false);
      setEditUser(null);
      setForm({ name: '', email: '', role: 'sales' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleResendInvite = async (userId, email) => {
    try {
      const r = await api.post(`/users/${userId}/resend-invite`);
      if (r.data.email_sent) {
        toast.success(`Invite resent to ${email}`);
      } else {
        toast.success('New invite link generated', {
          description: 'Email could not be sent. Copy the link manually.',
          action: { label: 'Copy Link', onClick: () => { navigator.clipboard.writeText(r.data.invite_link); toast.success('Copied'); } },
          duration: 8000,
        });
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    await api.delete(`/users/${id}`);
    toast.success('User deleted');
    load();
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'disabled' ? 'active' : 'disabled';
    await api.put(`/users/${user.id}`, { status: newStatus });
    toast.success(`${user.name} ${newStatus === 'disabled' ? 'disabled' : 'reactivated'}`);
    load();
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ name: user.name, email: user.email, role: user.role });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Team Members</h2>
          <p className="text-sm text-slate-500">{users.length} users</p>
        </div>
        <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => { setEditUser(null); setForm({ name: '', email: '', role: 'sales' }); setDialogOpen(true); }}
          data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1" /> Invite User
        </Button>
      </div>

      <Card className="border-slate-100">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Email</TableHead>
              <TableHead className="font-semibold text-slate-700">Role</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => {
              const st = STATUS_BADGES[u.status] || STATUS_BADGES.active;
              const StIcon = st.icon;
              return (
                <TableRow key={u.id} className="hover:bg-sky-50/30" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-sky-700">{u.name?.charAt(0)}</span>
                      </div>
                      <span className="font-medium text-slate-800">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs font-semibold ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-600'}`} onClick={() => setSelectedRole(selectedRole === u.role ? null : u.role)}>
                      {roles[u.role]?.label || u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] gap-1 ${st.className}`}>
                      <StIcon className="w-3 h-3" /> {st.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {u.status === 'invited' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Resend Invite" onClick={() => handleResendInvite(u.id, u.email)} data-testid={`resend-${u.id}`}>
                          <Send className="w-3.5 h-3.5 text-amber-500" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      {u.status !== 'disabled' && u.role !== 'admin' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Disable" onClick={() => handleToggleStatus(u)} data-testid={`disable-${u.id}`}>
                          <XCircle className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      )}
                      {u.status === 'disabled' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Reactivate" onClick={() => handleToggleStatus(u)} data-testid={`reactivate-${u.id}`}>
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(u.id, u.name)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Role Details Panel */}
      {selectedRole && roles[selectedRole] && (
        <Card className="border-sky-200 bg-sky-50/50 animate-fade-in">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-sky-600" />
              <CardTitle className="text-sm text-sky-800">{roles[selectedRole].label} — Permissions</CardTitle>
            </div>
            <p className="text-xs text-sky-600">{roles[selectedRole].description}</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {(roles[selectedRole].permissions || []).map(p => (
                <Badge key={p} variant="outline" className="text-[10px] bg-white">{p}</Badge>
              ))}
            </div>
            <div className="mt-3">
              <p className="text-[10px] text-slate-500 font-semibold mb-1">Visible Pages:</p>
              <div className="flex gap-1.5">
                {(roles[selectedRole].nav_items || []).map(n => (
                  <Badge key={n} className="bg-sky-100 text-sky-700 text-[10px]">{n}</Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Overview Cards */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-3">Role Overview</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(roles).map(([key, role]) => {
            const count = users.filter(u => u.role === key).length;
            return (
              <Card key={key} className={`border cursor-pointer transition-all hover:shadow-md ${selectedRole === key ? 'border-sky-400 ring-1 ring-sky-200' : 'border-slate-100'}`}
                onClick={() => setSelectedRole(selectedRole === key ? null : key)} data-testid={`role-card-${key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={`text-xs font-semibold ${ROLE_COLORS[key] || 'bg-slate-100'}`}>{role.label}</Badge>
                    <span className="text-xs font-mono text-slate-400">{count} user{count !== 1 ? 's' : ''}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{role.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editUser ? 'Edit User' : 'Invite New Team Member'}</DialogTitle>
            {!editUser && <p className="text-xs text-slate-500 mt-1">An invite email will be sent with a link to set their password.</p>}
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="user-form-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="user-form-email" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(roles).map(([key, role]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${ROLE_COLORS[key]?.split(' ')[0] || 'bg-slate-400'}`} />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {roles[form.role] && (
                <p className="text-[10px] text-slate-500 mt-1">{roles[form.role].description}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleSave}
              disabled={!form.name || !form.email} data-testid="user-form-save-btn">
              {editUser ? 'Update User' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
